import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    MDBAlert,
    MDBBtn,
    MDBContainer,
    MDBRow,
    MDBCol,
    MDBSpinner,
    MDBModalFooter,
    MDBIcon,
    MDBModal,
    MDBModalDialog,
    MDBModalContent,
    MDBModalHeader,
    MDBModalTitle,
    MDBModalBody
} from 'mdb-react-ui-kit';
import { idToSlug } from './utils/slug';
import { sendMemberwaiverUrl, getWaiverSettingsbyGymid } from "./services/memberApiServices";
import { createMemberFromWaiver, getBusinessName, getMemberTransactions, updateMemberCredentials, waiverStatus } from './services/apiServices';
import Popup from './components/Popup';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';

const QrWaiverPage = () => {
    const [loading, setLoading] = useState(false);
    const [linkStatus, setLinkStatus] = useState('');
    const [sendLink, setSendLink] = useState(false);
    const [waiverForm, setWaiverForm] = useState({});
    const [businessData, setBusinessData] = useState(null);
    const [apiError, setApiError] = useState(false);
    const [showMissingDetailsPopup, setShowMissingDetailsPopup] = useState(false);
    const [memberData, setMemberData] = useState({
        email: '',
        phonenumber: ''
    });
    const [updateError, setUpdateError] = useState("");
    const [showWaiverModal, setShowWaiverModal] = useState(false);
    const [waiverQrLink, setWaiverQrLink] = useState("");
    // ID of a newly created member (notInCurrentGym flow) — used for polling
    const [newlyCreatedMemberId, setNewlyCreatedMemberId] = useState(null);

    const navigate = useNavigate();
    const location = useLocation();

    const memberId = location.state?.memberId;
    const homeGymMemberId = location.state?.homeGymMemberId;
    const homeGymId = location.state?.homeGymId;
    const partnerGymId = location.state?.partnerGymId;
    const memberInfo = location.state?.memberInfo;
    const activeClass = location.state?.activeClass;
    const checkpage = location.state?.checkpage;

    const [memberTransactions, setMemberTransactions] = useState([]);


    useEffect(() => {
        if (!memberId) {
            navigate("/qrscanner");
        }
    }, [memberId]);


    useEffect(() => {
        if (memberInfo) {
            if (memberInfo?.notInCurrentGym) {
                setMemberData({
                    email: "",
                    phonenumber: ""
                });
            } else {
                setMemberData({
                    email: memberInfo?.email || "",
                    phonenumber: memberInfo?.number || ""
                });
            }
        }
    }, [memberInfo]);


    // Fetch waiver settings
    useEffect(() => {
        const fetchWaiverSettings = async () => {
            const gymIdToUse = partnerGymId || homeGymId;

            if (!gymIdToUse) return;

            try {
                setLoading(true);

                const waiverRes = await getWaiverSettingsbyGymid(gymIdToUse);

                if (waiverRes && waiverRes !== "false") {
                    const waiver = await waiverRes.json();

                    if (waiver?.uniqueNumber) {
                        setWaiverForm(waiver);
                    } else {
                        console.log("Waiver settings fetched but missing uniqueNumber");
                        setWaiverForm({});
                    }
                }
            } catch (error) {
                console.log("Error fetching waiver settings:", error);
                setWaiverForm({});
            } finally {
                setLoading(false);
            }
        };

        fetchWaiverSettings();
    }, [homeGymId, partnerGymId, memberInfo]);


    useEffect(() => {
        const fetchTransactions = async () => {
            if (!memberId) return;
            setLoading(true);
            try {
                const response = await getMemberTransactions(memberId);

                if (response && response.ok) {
                    const data = await response.json();

                    if (Array.isArray(data) && data.length > 0) {
                        setMemberTransactions(data);

                        console.log("Member found in current gym - proceeding with existing flow");

                    } else {
                        console.log("No transactions found for member");
                        setMemberTransactions([]);
                    }
                } else {
                    console.log("Failed to fetch member transactions");
                }
            } catch (err) {
                console.log("Error fetching member transactions", err);
            } finally {
                setLoading(false);
            }
        };

        fetchTransactions();
    }, [memberId, navigate]);

    // Fetch business data
    useEffect(() => {
        const fetchBusinessData = async () => {
            try {
                const gymId = homeGymId;

                if (gymId) {
                    const payload = { bId: gymId };
                    const result = await getBusinessName(payload);

                    if (result && result !== "false") {
                        const jsonData = await result.json();
                        setBusinessData(jsonData);
                    } else {
                        setApiError(true);
                    }
                }
            } catch (error) {
                console.log("Error fetching business name:", error);
                setApiError(true);
            }
        };
        fetchBusinessData();
    }, [homeGymId]);




    // Encrypt ID function
    const encryptId = (id) => {
        return btoa(String(id));
    };

    // Generate waiver link — always uses partnerGymId
    const generateWaiverLink = useCallback(() => {
        const baseUrl = window.location.origin;
        if (!waiverForm?.uniqueNumber || !partnerGymId) return '';

        const encryptedId = encryptId(waiverForm.uniqueNumber);

        if (memberInfo?.notInCurrentGym) {
            // Member not yet in partner gym — no mId, backend creates them on submission
            return `${baseUrl}/waiver?id=${encryptedId}&gymId=${partnerGymId}&qrCheckin=true`;
        }

        // Member exists in partner gym (partnerWaiverRequired) — link to their partner gym record
        if (!memberId) return '';
        const slug = idToSlug(memberId);
        return `${baseUrl}/waiver?id=${encryptedId}&mId=${slug}&gymId=${partnerGymId}&qrCheckin=true`;
    }, [memberInfo, memberId, waiverForm?.uniqueNumber, partnerGymId]);



    // Handle view waiver
    const handleViewWaiver = () => {
        const link = generateWaiverLink();
        if (link) {
            setWaiverQrLink(link);
            setShowWaiverModal(true);

            if (!window.waiverInterval) {
                if (memberInfo?.notInCurrentGym) {
                    // Member doesn't exist at partner gym yet — poll by phone
                    startWaiverStatusPolling(null, memberData.phonenumber || null);
                } else {
                    startWaiverStatusPolling();
                }
            }
        }
    };

    // Update member data
    const updateMemberData = (updates) => {
        setMemberData(prev => ({ ...prev, ...updates }));
    };

    // Update member credentials and send waiver
    const updateAndSendWaiver = async () => {
        try {
            setUpdateError("");

            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!memberData.email.trim()) {
                setUpdateError("Email address is required.");
                return;
            }
            if (!emailRegex.test(memberData.email.trim())) {
                setUpdateError("Please enter a valid email address.");
                return;
            }

            if (!memberData.phonenumber) {
                setUpdateError("Phone number is required.");
                return;
            }
            if (!isValidPhoneNumber(memberData.phonenumber)) {
                setUpdateError("Please enter a valid 10-digit phone number.");
                return;
            }

            setLoading(true);


            const link = generateWaiverLink();
            console.log("link", link)
            if (!link) {
                setUpdateError("Failed to generate waiver link.");
                return;
            }

            if (memberInfo?.notInCurrentGym) {
                try {
                    const updates = {
                        gymid: partnerGymId,
                        email: memberData.email,
                        name: `${memberInfo?.fname || ""} ${memberInfo?.lname || ""}`.trim(),
                        phonenumber: memberData.phonenumber,
                        link: link,
                    };

                    const response = await createMemberFromWaiver(updates);

                    if (response && response !== "false") {
                        const data = await response.json(); 

                        if (data.success) {
                            setLinkStatus("success");
                            setSendLink(true);
                            setShowMissingDetailsPopup(false);

                            // If backend returns the new member's ID, start polling so we
                            // navigate automatically once the member submits the waiver.
                            const createdId = data.memberId || data._id || data.newMemberId;
                            if (createdId) {
                                setNewlyCreatedMemberId(createdId);
                                startWaiverStatusPolling(createdId, null);
                            } else {
                                // Backend didn't return a new member ID — poll by phone until the
                                // record appears (created when the member submits the waiver form).
                                startWaiverStatusPolling(null, memberData.phonenumber || null);
                            }
                            setTimeout(() => handleNavigate(), 3000);
                        } else {
                            setLinkStatus("danger");
                            setUpdateError("Failed to send waiver link. Please try again.");
                        }
                    } else {
                        setLinkStatus("danger");
                        setUpdateError("Failed to send waiver link. Please try again.");
                    }
                } catch (err) {
                    setLinkStatus("danger");
                    setUpdateError("Error creating new member via waiver.");
                } finally {
                    setLoading(false);
                }
                return;
            }

            const updates = {
                id: memberId,
                email: memberData.email,
                phonenumber: memberData.phonenumber
            };

            const response = await updateMemberCredentials(updates);

            if (response && response.status === 200) {
                await response.json();
                setShowMissingDetailsPopup(false);
                await sendWaiverLinkToMember();

            } else {
                setUpdateError("Failed to update member details. Please try again.");
            }
        } catch (err) {
            setUpdateError("Error updating contact details. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Send waiver link to member
    const sendWaiverLinkToMember = async () => {
        try {
            setLoading(true);
            setLinkStatus('');
            if (memberInfo?.notInCurrentGym) {
                console.log("Skipping sendWaiverLinkToMember for non-current gym member");
                return;
            }

            if (!memberId || !homeGymId) {
                alert("Missing required information");
                return;
            }

            const link = generateWaiverLink();
            if (!link) {
                setLinkStatus('danger');
                setSendLink(true);
                return;
            }

            const payload = {
                link: link,
                id: memberId,
                gymid: partnerGymId,
                centername: businessData?.businessName || "Our Gym",
                number: memberData.phonenumber || memberInfo?.number || "",
                email: memberData.email || memberInfo?.email || "",
            };

            const response = await sendMemberwaiverUrl(payload);

            if (response && response !== "false") {
                const data = await response.json();
                if (data.success) {
                    setLinkStatus('success');
                    setSendLink(true);

                    startWaiverStatusPolling();
                    setTimeout(() => handleNavigate(), 3000);
                } else {
                    setLinkStatus('danger');
                    setSendLink(true);
                }
            } else {
                setLinkStatus('danger');
                setSendLink(true);
            }
        } catch (err) {
            console.log("Error sending waiver:", err);
            setLinkStatus('danger');
            setSendLink(true);
        } finally {
            setLoading(false);
        }
    };

    // Handle send waiver (main function)
    const handleSendWaiver = async () => {
        if (!memberInfo?.number || memberInfo.number.trim() === "" || memberInfo?.notInCurrentGym) {
            setShowMissingDetailsPopup(true);
            return;
        }
        await sendWaiverLinkToMember();
    };

    // Polling for waiver status.
    // overrideMemberId — use when we have a fresh member ID (e.g. returned by backend).
    // overridePhone    — use for notInCurrentGym flow: the member record doesn't exist
    //                    yet (it's created when they submit the waiver), so we poll by
    //                    phone+gym until the record appears with iswaiververified=true.
    const startWaiverStatusPolling = (overrideMemberId = null, overridePhone = null) => {
        if (window.waiverInterval) {
            clearInterval(window.waiverInterval);
        }

        // For notInCurrentGym, the page-level memberId is from the home gym and does not
        // exist at the partner gym — using it would cause polling to spin forever.
        // Only use an explicitly passed override ID in that flow.
        const idToUse = overrideMemberId ||
            (!memberInfo?.notInCurrentGym && memberId && memberId !== "-1" ? memberId : null);
        const phoneToUse = overridePhone || null;

        // Need at least one identifier
        if (!idToUse && !phoneToUse) {
            console.log("Polling skipped — no valid member ID or phone");
            return;
        }

        window.waiverInterval = setInterval(async () => {
            try {
                const payload = { gymId: partnerGymId };
                if (idToUse)    payload.memberId = idToUse;
                if (phoneToUse) payload.phone    = phoneToUse;

                const response = await waiverStatus(payload);
                const data = await response.json();

                if (data?.submitted) {
                    clearInterval(window.waiverInterval);
                    window.waiverInterval = null;
                    setShowWaiverModal(false);
                    setShowMissingDetailsPopup(false);

                    if (checkpage === true) {
                        navigate('/checkin');
                    } else {
                        const planInfo = localStorage.getItem("planInfo")
                            ? JSON.parse(localStorage.getItem("planInfo"))
                            : null;
                        const txMemberId = homeGymMemberId || memberId;
                        const txResponse = await getMemberTransactions(txMemberId);
                        const homeGymData =
                            txResponse && txResponse.ok
                                ? await txResponse.json()
                                : memberTransactions;
                        navigate('/qrcheckin', {
                            state: {
                                data: homeGymData,
                                memberId: txMemberId,
                                homeGymId,
                                currentGymId: partnerGymId,
                                isMultiGym: true,
                                planInfo,
                                activeClass
                            }
                        });
                    }
                }
            } catch (err) {
                console.log("Error checking waiver status", err);
            }
        }, 10000);
    };

    // Cleanup intervals on unmount
    useEffect(() => {
        return () => {
            if (window.waiverInterval) {
                clearInterval(window.waiverInterval);
            }
        };
    }, []);


    const safeCloseModal = (setter) => {
        // Remove focus from any element inside the modal
        if (document.activeElement && typeof document.activeElement.blur === "function") {
            document.activeElement.blur();
        }
        // Then update state
        setter(false);
    };

    const handleCloseMissingDetails = () => {
        safeCloseModal(setShowMissingDetailsPopup);
    };

    // Close waiver QR modal safely
    const handleCloseWaiverModal = () => {
        safeCloseModal(setShowWaiverModal);
    };

    const handleDownloadWaiverQR = () => {
        if (waiverQrLink) {
            const link = document.createElement("a");
            link.href = `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(waiverQrLink)}&size=300x300`;
            link.download = "waiver_qrcode.png";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const handleNavigate = () => {
        if (checkpage === true) {
            navigate('/checkin');
        } else {
            navigate('/qrscanner');
        }
    };



    return (
        <MDBContainer className="mt-5">
            <MDBRow className="justify-content-center">
                <MDBCol md={12} sm={12}>
                    <div className="text-center p-4 border rounded-3 shadow-sm square border border-warning">
                        <div>
                            <MDBIcon fas icon="exclamation-triangle" color="warning" size='3x' />
                            <h3 className="mb-2 mt-3">Waiver Required</h3>
                            <p className="text-muted mb-4">
                                You are registered with a partner gym. To check in here, please submit the waiver form for this gym. Once submitted, you can proceed with check-in.
                            </p>

                        </div>

                        {memberInfo && (
                            <div className="mb-4 p-3 bg-light rounded">
                                <div className='row'>
                                    <div className='col-12 col-md-6 text-left row mb-3 mb-md-0'>
                                        <label className='col-12 col-md-3'><b>Member</b></label>
                                        <label className='col-12 col-md-9'>{memberInfo.fname} {memberInfo.lname}</label>
                                    </div>
                                    <div className='col-12 col-md-6 text-left square border-start'>
                                        <div className='row d-flex mb-2'>
                                            <label className='col-12 col-md-3'><b>Home Gym</b></label>
                                            <label className='col-12 col-md-9'>{businessData?.businessName || 'Loading...'}</label>
                                        </div>
                                        <div className='d-flex row'>
                                            <label className='col-12 col-md-3'><b>Address</b></label>
                                            <label className='col-12 col-md-9'>{businessData?.address || 'Loading...'}</label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="d-flex justify-content-between gap-3 flex-column flex-md-row">
                            <MDBBtn
                                color="light"
                                onClick={handleNavigate}
                                className="mb-2 mb-md-0"
                            >
                                Go Back
                            </MDBBtn>
                            <div className='d-flex gap-2 flex-column flex-md-row'>
                                <MDBBtn
                                    color="primary"
                                    size="lg"
                                    onClick={handleSendWaiver}
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <MDBSpinner size="sm" className="me-2" />
                                            Sending Waiver Link...
                                        </>
                                    ) : (
                                        'Send Waiver'
                                    )}
                                </MDBBtn>

                                <MDBBtn
                                    color="secondary"
                                    size="lg"
                                    onClick={handleViewWaiver}
                                    disabled={!waiverForm.uniqueNumber}
                                >
                                    View Waiver
                                </MDBBtn>
                            </div>
                        </div>
                    </div>
                </MDBCol>
            </MDBRow>

            {/* Status Alert */}
            <MDBAlert
                color={linkStatus}
                autohide
                position='top-center'
                delay={5000}
                appendToBody
                open={sendLink}
                onClose={() => setSendLink(false)}
            >
                {linkStatus === 'success'
                    ? "Waiver sent successfully! Please check your mobile."
                    : "Failed to send waiver. Please try again."}
            </MDBAlert>

            {/* Missing Details Popup */}
            {showMissingDetailsPopup && (
                <Popup
                    title="Enter Contact Details to Send the Waiver"
                    content={
                        <>
                            {updateError && (
                                <div className="alert alert-danger" role="alert">
                                    {updateError}
                                </div>
                            )}

                            <div className="mb-3">
                                <label className="form-label fw-bold">Email</label>
                                <input
                                    className="form-control"
                                    type="email"
                                    value={memberData.email}
                                    onChange={(e) => updateMemberData({ email: e.target.value })}
                                    placeholder="Enter email address"
                                />
                            </div>

                            <div className="mb-3">
                                <label className="form-label fw-bold">
                                    Phone Number <span className="text-danger">*</span>
                                </label>
                                <PhoneInput
                                    className="form-control"
                                    defaultCountry="IN"
                                    placeholder="Enter phone number"
                                    value={memberData.phonenumber}

                                    onChange={(value) => updateMemberData({ phonenumber: value })}
                                />
                            </div>

                            <MDBModalFooter className="mt-4">
                                <MDBBtn color="secondary" onClick={handleCloseMissingDetails}>
                                    Cancel
                                </MDBBtn>
                                <MDBBtn
                                    color="primary"
                                    onClick={updateAndSendWaiver}
                                    disabled={
                                        loading ||
                                        !memberData.email.trim() ||
                                        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(memberData.email.trim()) ||
                                        !memberData.phonenumber ||
                                        !isValidPhoneNumber(memberData.phonenumber)
                                    }
                                >
                                    {loading ? (
                                        <>
                                            <MDBSpinner size="sm" className="me-2" />
                                            Saving & Sending...
                                        </>
                                    ) : (
                                        'Send Waiver'
                                    )}
                                </MDBBtn>
                            </MDBModalFooter>
                        </>
                    }
                    handleClose={handleCloseMissingDetails}
                />
            )}


            {/* Waiver QR Code Modal */}
            <MDBModal open={showWaiverModal} setOpen={setShowWaiverModal} tabIndex='-1'>
                <MDBModalDialog>
                    <MDBModalContent>
                        <MDBModalHeader>
                            <MDBModalTitle>Waiver QR Code</MDBModalTitle>
                            <MDBBtn className='btn-close' color='none' onClick={handleCloseWaiverModal}></MDBBtn>
                        </MDBModalHeader>
                        <MDBModalBody className="text-center">
                            {waiverQrLink ? (
                                <>
                                    <img
                                        src={`https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(waiverQrLink)}&size=300x300`}
                                        alt="Waiver QR Code"
                                        className="img-fluid"
                                        style={{ maxWidth: '300px', maxHeight: '300px' }}
                                    />
                                    <p className="mt-3 text-muted">
                                        Scan this QR code to access the waiver form
                                    </p>
                                </>
                            ) : (
                                <span>No Waiver QR found</span>
                            )}
                        </MDBModalBody>
                        <MDBModalFooter>
                            {/* <MDBBtn color="primary" onClick={handleDownloadWaiverQR} disabled={!waiverQrLink}>
                                Download QR
                            </MDBBtn> */}
                            <MDBBtn color="secondary" onClick={handleCloseWaiverModal}>
                                Close
                            </MDBBtn>

                        </MDBModalFooter>
                    </MDBModalContent>
                </MDBModalDialog>
            </MDBModal>
        </MDBContainer>
    );
};

export default QrWaiverPage;